// PromptGenerator/hooks/usePromptEditor.ts
import { useState, useCallback, useMemo } from 'react';
import { getApiUrl } from '../../../config/api';
import { useAuth } from '../../../context/Auth/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { PromptData } from './useProjectData'; // Updated import path

export const usePromptEditor = (
    project: any, // Use Project type from useProjectData
    setProject: (project: any) => void,
    selectedPrompt: PromptData | null,
    setPrompts: (prompts: PromptData[]) => void,
    prompts: PromptData[]
) => {
    const { token } = useAuth();
    const { showNotification } = useNotification();
    
    // Project Title State
    const [editingTitle, setEditingTitle] = useState(false);
    const [editedTitle, setEditedTitle] = useState('');
    
    // Prompt Title State
    const [editingPromptTitle, setEditingPromptTitle] = useState<string | null>(null);
    const [editedPromptTitle, setEditedPromptTitle] = useState('');
    
    // Prompt Content State
    const [editingPrompt, setEditingPrompt] = useState(false);
    const [editedPrompt, setEditedPrompt] = useState('');
    
    // Original Idea State
    const [editingOriginalIdea, setEditingOriginalIdea] = useState(false);
    const [editedOriginalIdea, setEditedOriginalIdea] = useState('');

    // Delete State (Used for single prompt spinner)
    const [deletingPromptId, setDeletingPromptId] = useState<string | null>(null);
    

    // --- Project Title Handlers ---
    const handleProjectTitleEdit = useCallback(() => {
        if (project?.title) {
            setEditedTitle(project.title);
            setEditingTitle(true);
        }
    }, [project]);

    const handleProjectTitleSave = useCallback(async () => {
        if (!project || !editedTitle.trim() || editedTitle.trim() === project.title) {
            setEditingTitle(false);
            setEditedTitle('');
            return;
        }

        try {
            const response = await fetch(getApiUrl(`/projects/${project.id}`), {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ title: editedTitle.trim() }),
            });

            if (response.ok) {
                setProject({ ...project, title: editedTitle.trim() });
                setEditingTitle(false);
            }
        } catch (error) {
            console.error('Failed to save project title:', error);
            showNotification({ title: 'Error', message: 'Failed to save project title', type: 'error' });
        }
    }, [project, editedTitle, setProject, token, showNotification]);

    const handleProjectTitleCancel = useCallback(() => {
        setEditingTitle(false);
        setEditedTitle('');
    }, []);

    const handleProjectTitleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleProjectTitleSave();
        else if (e.key === 'Escape') handleProjectTitleCancel();
    }, [handleProjectTitleSave, handleProjectTitleCancel]);

    // --- Prompt Title Handlers ---
    const handlePromptTitleEdit = useCallback((promptId: string, currentTitle: string) => {
        setEditingPromptTitle(promptId);
        setEditedPromptTitle(currentTitle);
    }, []);

    const handlePromptTitleSave = useCallback(async (promptId: string) => {
        if (!editedPromptTitle.trim()) {
            setEditingPromptTitle(null);
            setEditedPromptTitle('');
            return;
        }
        
        try {
            const response = await fetch(getApiUrl('/prompt-generator/update-title'), {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ promptId: promptId, title: editedPromptTitle.trim() }),
            });
            const result = await response.json();
            
            if (result.success) {
                setPrompts(prompts.map(p => 
                    p.id === promptId ? { ...p, title: editedPromptTitle.trim() } : p
                ));
                setEditingPromptTitle(null);
                setEditedPromptTitle('');
            }
        } catch (error) {
            console.error('Failed to save prompt title:', error);
            showNotification({ title: 'Error', message: 'Failed to save prompt title', type: 'error' });
        }
    }, [editedPromptTitle, prompts, setPrompts, token, showNotification]);

    const handlePromptTitleCancel = useCallback(() => {
        setEditingPromptTitle(null);
        setEditedPromptTitle('');
    }, []);

    const handlePromptTitleKeyPress = useCallback((e: React.KeyboardEvent, promptId: string) => {
        if (e.key === 'Enter') handlePromptTitleSave(promptId);
        else if (e.key === 'Escape') handlePromptTitleCancel();
    }, [handlePromptTitleSave, handlePromptTitleCancel]);

    // --- Generated Prompt Content Handlers ---
    const handlePromptEdit = useCallback(() => {
        if (selectedPrompt?.generatedPrompt) {
            setEditedPrompt(selectedPrompt.generatedPrompt);
            setEditingPrompt(true);
        }
    }, [selectedPrompt]);

    const handlePromptSave = useCallback(async (
        loadPrompts: () => Promise<void>,
        updatePromptsList?: (updatedPrompt: PromptData) => void
    ) => {
        if (!selectedPrompt || !editedPrompt.trim()) {
            setEditingPrompt(false);
            setEditedPrompt('');
            return;
        }

        try {
            const response = await fetch(getApiUrl('/prompt-generator/update'), {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ promptId: selectedPrompt.id, generatedPrompt: editedPrompt.trim() }),
            });

            if (response.ok) {
                // Update the selected prompt immediately for instant UI update
                if (updatePromptsList) {
                    const updatedPrompt = {
                        ...selectedPrompt,
                        generatedPrompt: editedPrompt.trim(),
                        updatedAt: new Date().toISOString()
                    };
                    updatePromptsList(updatedPrompt);
                }
                
                // Close editing mode
                setEditingPrompt(false);
                setEditedPrompt('');
                
                // Reload the full list to sync with backend
                await loadPrompts();
            }
        } catch (error) {
            console.error('Failed to save prompt content:', error);
            showNotification({ title: 'Error', message: 'Failed to save prompt content', type: 'error' });
        }
    }, [selectedPrompt, editedPrompt, token, showNotification]);

    const handlePromptCancel = useCallback(() => {
        setEditingPrompt(false);
        setEditedPrompt('');
    }, []);

    // --- Original Idea Handlers ---
    const handleOriginalIdeaEdit = useCallback(() => {
        if (selectedPrompt?.userSelections?.userIdea) {
            setEditedOriginalIdea(selectedPrompt.userSelections.userIdea);
            setEditingOriginalIdea(true);
        }
    }, [selectedPrompt]);

    const handleOriginalIdeaSave = useCallback(async (updatePromptsList: (updatedPrompt: PromptData) => void) => {
        if (!selectedPrompt || !editedOriginalIdea.trim()) {
            setEditingOriginalIdea(false);
            setEditedOriginalIdea('');
            return;
        }
        
        try {
            const response = await fetch(getApiUrl('/prompt-generator/update-idea'), {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ promptId: selectedPrompt.id, userIdea: editedOriginalIdea.trim() }),
            });
            const result = await response.json();
            
            if (result.success) {
                const updatedPrompt = {
                    ...selectedPrompt,
                    userSelections: {
                        ...selectedPrompt.userSelections,
                        userIdea: editedOriginalIdea.trim()
                    }
                };
                updatePromptsList(updatedPrompt);
                setEditingOriginalIdea(false);
                setEditedOriginalIdea('');
            }
        } catch (error) {
            console.error('Failed to save original idea:', error);
            showNotification({ title: 'Error', message: 'Failed to save original idea', type: 'error' });
        }
    }, [selectedPrompt, editedOriginalIdea, token, showNotification]);

    const handleOriginalIdeaCancel = useCallback(() => {
        setEditingOriginalIdea(false);
        setEditedOriginalIdea('');
    }, []);


    return {
        editingTitle, editedTitle, setEditedTitle, handleProjectTitleEdit, handleProjectTitleSave, handleProjectTitleCancel, handleProjectTitleKeyPress,
        editingPromptTitle, editedPromptTitle, setEditedPromptTitle, handlePromptTitleEdit, handlePromptTitleSave, handlePromptTitleCancel, handlePromptTitleKeyPress,
        editingPrompt, editedPrompt, setEditedPrompt, handlePromptEdit, handlePromptSave, handlePromptCancel,
        editingOriginalIdea, editedOriginalIdea, setEditedOriginalIdea, handleOriginalIdeaEdit, handleOriginalIdeaSave, handleOriginalIdeaCancel,
        deletingPromptId, setDeletingPromptId
    };
};